import React, { useState } from "react";

// region External libraries
import { Checkbox, FormControl, FormControlLabel, TextField, Button as ButtonMUI } from "@mui/material";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { Controller, UseFormReturn } from "react-hook-form";
import { useMutation } from "@apollo/client";
// endregion External libraries
// region Languages
import { FormUserMessages, GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Helpers
import { handleErrorMessage } from "src/store/helpers/handleErrorMessage";
// endregion Helpers
// region Atoms
import UserIcon from "@atoms/UserIcon";
import ActionDialog from "@atoms/ActionDialog";
// endregion Atoms
// region Molecules
// endregion Molecules
// region Organisms
// endregion Organisms
// region Pages
import { IFormUserInputs } from "@pages/User";
// endregion Pages
import { TUserDeleteMutations, userDelete } from "@graphql/groups/user/mutation.graphql";
import { UserMutationsDeleteArgs, ECommandResultStatus } from "@graphql/helpers/types.graphql";
import { useIdentity, PERMISSIONS } from "src/hooks/useIdentity";
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IFormUserProps {
  controlForms: UseFormReturn<IFormUserInputs>;
  mode: "CREATE" | "EDIT";
  currentUserId?: string | null;
  onDelete?: () => void;
}

const FormUser: React.FC<IFormUserProps> = ({ controlForms, mode, currentUserId, onDelete }) => {
  // region hooks
  const { addToast } = useToast();
  const { validateUserAccess } = useIdentity();
  const {
    control,
    setValue,
    watch,
    formState: { errors: formErrors }
  } = controlForms;
  const { t } = useTranslation();
  // endregion hooks

  // region constants
  const userImage = watch("imageUrl");
  const identityValidations = {
    userInactivate: PERMISSIONS.USER_RIGHTS_CONTROL.USERS.INACTIVATE,
    userDelete: PERMISSIONS.USER_RIGHTS_CONTROL.USERS.DELETE
  };
  // endregion constants

  const [openDeleteUserConfirmationModal, setOpenDeleteUserConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [MutationDeleteUser] = useMutation<TUserDeleteMutations, UserMutationsDeleteArgs>(userDelete());

  // region handlers
  const handleInputFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    // TODO: Implement the logic to upload the image to the server
    if (file) setValue("imageUrl", URL.createObjectURL(file));
  };
  // endregion handlers

  const deleteCurrentUser = async () => {
    try {
      setIsLoading(true);

      const mutation = await MutationDeleteUser({
        variables: {
          id: currentUserId as string
        }
      });

      if (mutation.data?.user?.delete?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({ type: "success", title: mutation.data.user.delete.message });

      onDelete && onDelete();
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });
    } finally {
      setOpenDeleteUserConfirmationModal(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Styled.FormContainer>
        <Controller
          name="email"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(FormUserMessages.fieldEmail)}
              variant="outlined"
              error={!!formErrors.email}
              helperText={handleErrorMessage(formErrors.email, t)}
              className="default-field"
              size="small"
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(FormUserMessages.fieldName)}
              variant="outlined"
              error={!!formErrors.name}
              helperText={handleErrorMessage(formErrors.name, t)}
              className="default-field"
              size="small"
            />
          )}
        />
        <Controller
          name="imageUrl"
          control={control}
          defaultValue=""
          render={({ field }) => (
            // TODO: First version will not have the image upload feature
            <FormControl error={!!formErrors.imageUrl} className="default-field" size="small" sx={{ display: "none" }}>
              <Styled.UserIconContainer>
                <UserIcon
                  userImg={userImage}
                  blockMoreInfo
                />
                {userImage
                  ? (
                    <ButtonMUI
                      variant="contained"
                      id="buttonDelete"
                      onClick={() => setValue("imageUrl", "")}
                    >
                      {t(FormUserMessages.removeImage)}
                    </ButtonMUI>
                  )
                  : (
                    <ButtonMUI variant="contained" component="label" id="buttonFile">
                      {t(FormUserMessages.addImage)}
                      <input hidden type="file" onChange={handleInputFileChange} accept="image/*" />
                    </ButtonMUI>
                  )}
              </Styled.UserIconContainer>
            </FormControl>
          )}
        />
        <Controller
          name="position"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(FormUserMessages.fieldPosition)}
              variant="outlined"
              error={!!formErrors.position}
              helperText={handleErrorMessage(formErrors.position, t)}
              className="default-field"
              size="small"
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(FormUserMessages.fieldDescription)}
              variant="outlined"
              error={!!formErrors.description}
              helperText={handleErrorMessage(formErrors.description, t)}
              className="default-field"
              multiline
              rows={3}
              size="small"
              sx={{ marginBottom: "20px" }}
            />
          )}
        />
        <Controller
          name="active"
          control={control}
          defaultValue
          render={({ field }) => (
            <FormControlLabel
              {...field}
              control={<Checkbox checked={field.value} size="small" />}
              label={t(field.value ? FormUserMessages.fieldActive : FormUserMessages.fieldInactive).toString()}
              className={!validateUserAccess(identityValidations.userInactivate) ? "no-permission" : ""}
              sx={{ visibility: mode === "EDIT" ? "visible" : "hidden" }}
              disabled={!validateUserAccess(identityValidations.userInactivate)}
            />
          )}
        />
        <Styled.FormButtons>
          {mode === "EDIT" && onDelete && (
          <Button
            text={t(GlobalMessages.buttonDelete)}
            type="button"
            className={`delete-button ${!validateUserAccess(identityValidations.userDelete) ? "no-permission" : ""}`}
            onClick={() => {
              if (!validateUserAccess(identityValidations.userDelete)) return;

              setOpenDeleteUserConfirmationModal(true);
            }}
            loading={isLoading}
            disabled={!validateUserAccess(identityValidations.userDelete)}
          />
          )}
        </Styled.FormButtons>
      </Styled.FormContainer>
      <ActionDialog
        open={openDeleteUserConfirmationModal}
        onClose={() => setOpenDeleteUserConfirmationModal(false)}
        title={t(FormUserMessages.deletionConfirmationTitleModal)}
        actions={[
          {
            text: t(GlobalMessages.noText),
            action: () => {
              setOpenDeleteUserConfirmationModal(false);
            }
          },
          {
            text: t(GlobalMessages.yesText),
            action: () => {
              deleteCurrentUser();
            }
          }
        ]}
      >
        {t(FormUserMessages.deletionConfirmationSubtitleModal)}
      </ActionDialog>
    </>
  );
};

FormUser.defaultProps = {
  currentUserId: null,
  onDelete: undefined
};

export default FormUser;